import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import performRequest from "network/perform-request";
import {
  baseDomain,
  competitionParticipantsExportRoute,
  competitionsRegistrationsExportRoute,
  competitionsRegistrationsRoute,
} from "network/api-routes";
import UserContext from "context/UserContext";
import EditParticipantModal from "../Tournament/EditParticipantModal";
import { adminEventRoute } from "constants/routes";
import {
  activeRegistrationFilters,
  EMPTY_REGISTRATION_FILTERS,
  registrationEmptyMessage,
  registrationFiltersFromSearch,
  registrationSearch,
  serializeRegistrationsRequest,
} from "./adminEventUtils";

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const CompetitionRegistrationsPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [filters, setFilters] = useState(() => registrationFiltersFromSearch(location.search));
  const [debouncedSearch, setDebouncedSearch] = useState(() => filters.search);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0 });
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState({
    competition_id: null,
    tournaments: [],
    account_available: false,
    account_id: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRegistrationId, setSelectedRegistrationId] = useState(
    () => new URLSearchParams(location.search).get("registration_id") || null,
  );
  const compactTable = useMediaQuery((theme) => theme.breakpoints.down("md"));

  useEffect(() => {
    const search = registrationSearch(filters, selectedRegistrationId);
    if (search === location.search) return;

    history.replace({ pathname: location.pathname, search });
  }, [filters, history, location.pathname, location.search, selectedRegistrationId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedSearch(filters.search), 350);
    return () => window.clearTimeout(timeoutId);
  }, [filters.search]);

  const requestFilters = useMemo(
    () => ({
      ...filters,
      search: debouncedSearch,
    }),
    [filters, debouncedSearch],
  );

  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await performRequest(
        "POST",
        competitionsRegistrationsRoute,
        serializeRegistrationsRequest({ competitionUuid: id, paginationModel, filters: requestFilters }),
        true,
        user,
      );
      setRows(response.data.data || []);
      setPagination(response.data.meta?.pagination || { total: 0 });
      setSummary(response.data.meta?.summary || null);
      setOptions(response.data.meta?.options || {
        competition_id: null,
        tournaments: [],
        account_available: false,
        account_id: null,
      });
    } catch (requestError) {
      setError(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [id, paginationModel, requestFilters, user]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  useEffect(() => {
    if (!selectedRegistrationId || selectedRow) return;

    const registration = rows.find((row) => String(row.id) === String(selectedRegistrationId));
    if (registration) setSelectedRow(registration);
  }, [rows, selectedRegistrationId, selectedRow]);

  const openRegistration = (registration) => {
    setSelectedRow(registration);
    setSelectedRegistrationId(registration.id);
  };

  const closeRegistration = () => {
    setSelectedRow(null);
    setSelectedRegistrationId(null);
  };

  const updateFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    setPaginationModel((current) => ({ ...current, page: 0 }));
  };

  const statusChip = (status) => {
    const config = {
      completed: { label: "Confirmada", color: "success" },
      "waiting-payment": { label: "Pendiente", color: "warning" },
      expired: { label: "Expirada", color: "default" },
    }[status] || { label: status, color: "default" };

    return <Chip size="small" label={intl.formatMessage({ id: config.label })} color={config.color} variant="outlined" />;
  };

  const columns = [
    {
      field: "created_at",
      headerName: intl.formatMessage({ id: "Fecha" }),
      minWidth: 135,
      flex: 1,
      valueFormatter: (value) => (value ? intl.formatDate(new Date(value), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"),
    },
    {
      field: "participant",
      headerName: intl.formatMessage({ id: "Participante" }),
      minWidth: 190,
      flex: 1.4,
      valueGetter: (value) => value?.name || intl.formatMessage({ id: "Desconocido" }),
    },
    {
      field: "tournament",
      headerName: intl.formatMessage({ id: "Torneo" }),
      minWidth: 160,
      flex: 1.1,
      valueGetter: (value) => value?.name || "—",
    },
    {
      field: "origin",
      headerName: intl.formatMessage({ id: "Origen" }),
      minWidth: 105,
      flex: 0.8,
      valueFormatter: (value) => intl.formatMessage({ id: value === "manual" ? "Manual" : "Online" }),
    },
    {
      field: "payment_type",
      headerName: intl.formatMessage({ id: "Forma de pago" }),
      minWidth: 135,
      flex: 1,
      valueFormatter: (value) => intl.formatMessage({ id: value === "online" ? "Pago Online" : "Efectivo / Otro" }),
    },
    {
      field: "status",
      headerName: intl.formatMessage({ id: "Estado" }),
      minWidth: 125,
      flex: 0.9,
      renderCell: ({ value }) => statusChip(value),
    },
    {
      field: "actions",
      headerName: intl.formatMessage({ id: "Acciones" }),
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            openRegistration(row);
          }}
        >
          {intl.formatMessage({ id: "Administrar" })}
        </Button>
      ),
    },
  ];

  const exportRegistrations = async () => {
    setExportMenuAnchor(null);
    setExporting(true);
    try {
      const response = await performRequest(
        "POST",
        competitionsRegistrationsExportRoute,
        { competition_uuid: id, ...activeRegistrationFilters(requestFilters) },
        true,
        user,
        null,
        {},
        false,
        "blob",
      );
      downloadBlob(response.data, `inscripciones-${id}.csv`);
    } catch (requestError) {
      enqueueSnackbar(intl.formatMessage({ id: "No se pudieron exportar las inscripciones." }), { variant: "error" });
    } finally {
      setExporting(false);
    }
  };

  const exportParticipants = async () => {
    setExportMenuAnchor(null);
    setExporting(true);
    try {
      const response = await performRequest(
        "GET",
        competitionParticipantsExportRoute(options.competition_id),
        null,
        true,
        user,
        null,
        {},
        false,
        "blob",
      );
      downloadBlob(response.data, `participantes-${id}.csv`);
    } catch (requestError) {
      enqueueSnackbar(intl.formatMessage({ id: "No se pudo exportar la lista de participantes." }), { variant: "error" });
    } finally {
      setExporting(false);
    }
  };

  const accountAction = options.account_available ? (
    <Button component="a" href={`${baseDomain}/account/set-account/${options.account_id}`} target="_blank" rel="noreferrer">
      {intl.formatMessage({ id: "Ver estado de cuenta asociada" })}
    </Button>
  ) : (
    <Button component={Link} to={`${adminEventRoute(id)}/edit`}>
      {intl.formatMessage({ id: "Configurar cuenta" })}
    </Button>
  );

  const hasActiveFilters = Object.values(filters).some(Boolean);
  const emptyMessage = registrationEmptyMessage(filters);

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1} alignItems={{ md: "center" }}>
        <Box>
          <Box component="h1" sx={{ typography: "h2", m: 0 }}>
            {intl.formatMessage({ id: "Inscripciones" })}
          </Box>
          {summary && (
            <Box component="p" sx={{ color: "text.secondary", m: 0 }}>
              {intl.formatMessage({ id: "{count} inscripciones" }, { count: summary.total })}
            </Box>
          )}
        </Box>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {accountAction}
          <Button
            variant="outlined"
            startIcon={<DownloadOutlinedIcon />}
            aria-controls={exportMenuAnchor ? "registration-export-menu" : undefined}
            aria-haspopup="menu"
            aria-expanded={Boolean(exportMenuAnchor)}
            onClick={(event) => setExportMenuAnchor(event.currentTarget)}
            disabled={exporting}
          >
            {intl.formatMessage({ id: "Exportar" })}
          </Button>
          <Menu
            id="registration-export-menu"
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={() => setExportMenuAnchor(null)}
          >
            <MenuItem onClick={exportRegistrations} disabled={exporting}>
              {intl.formatMessage({ id: "Exportar inscripciones" })}
            </MenuItem>
            <MenuItem onClick={exportParticipants} disabled={exporting || !options.competition_id}>
              {intl.formatMessage({ id: "Exportar participantes" })}
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} flexWrap="wrap" gap={1.5}>
          <TextField
            size="small"
            label={intl.formatMessage({ id: "Buscar" })}
            value={filters.search}
            onChange={(event) => updateFilter("search", event.target.value)}
            sx={{ minWidth: { md: 220 } }}
          />
          <TextField select size="small" label={intl.formatMessage({ id: "Torneo" })} value={filters.tournament_id} onChange={(event) => updateFilter("tournament_id", event.target.value)}>
            <MenuItem value="">{intl.formatMessage({ id: "Todos" })}</MenuItem>
            {options.tournaments.map((tournament) => (
              <MenuItem key={tournament.id} value={tournament.id}>{tournament.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label={intl.formatMessage({ id: "Estado" })} value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>
            <MenuItem value="">{intl.formatMessage({ id: "Todos" })}</MenuItem>
            <MenuItem value="completed">{intl.formatMessage({ id: "Confirmada" })}</MenuItem>
            <MenuItem value="waiting-payment">{intl.formatMessage({ id: "Pendiente" })}</MenuItem>
            <MenuItem value="expired">{intl.formatMessage({ id: "Expirada" })}</MenuItem>
          </TextField>
          <TextField select size="small" label={intl.formatMessage({ id: "Origen" })} value={filters.origin} onChange={(event) => updateFilter("origin", event.target.value)}>
            <MenuItem value="">{intl.formatMessage({ id: "Todos" })}</MenuItem>
            <MenuItem value="online">{intl.formatMessage({ id: "Online" })}</MenuItem>
            <MenuItem value="manual">{intl.formatMessage({ id: "Manual" })}</MenuItem>
          </TextField>
          <TextField select size="small" label={intl.formatMessage({ id: "Forma de pago" })} value={filters.payment_type} onChange={(event) => updateFilter("payment_type", event.target.value)}>
            <MenuItem value="">{intl.formatMessage({ id: "Todos" })}</MenuItem>
            <MenuItem value="online">{intl.formatMessage({ id: "Pago Online" })}</MenuItem>
            <MenuItem value="other">{intl.formatMessage({ id: "Efectivo / Otro" })}</MenuItem>
          </TextField>
          <TextField size="small" type="date" label={intl.formatMessage({ id: "Desde" })} InputLabelProps={{ shrink: true }} value={filters.date_from} onChange={(event) => updateFilter("date_from", event.target.value)} />
          <TextField size="small" type="date" label={intl.formatMessage({ id: "Hasta" })} InputLabelProps={{ shrink: true }} value={filters.date_to} onChange={(event) => updateFilter("date_to", event.target.value)} />
          {hasActiveFilters && (
            <Button
              onClick={() => {
                setFilters(EMPTY_REGISTRATION_FILTERS);
                setPaginationModel((current) => ({ ...current, page: 0 }));
              }}
            >
              {intl.formatMessage({ id: "Limpiar filtros" })}
            </Button>
          )}
        </Stack>
      </Paper>

      {error ? (
        <Alert severity="error" action={<Button color="inherit" startIcon={<RefreshOutlinedIcon />} onClick={fetchRegistrations}>{intl.formatMessage({ id: "Reintentar" })}</Button>}>
          {intl.formatMessage({ id: "No se pudieron cargar las inscripciones." })}
        </Alert>
      ) : loading && rows.length === 0 ? (
        <Skeleton variant="rounded" height={340} />
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: "center" }}>{intl.formatMessage({ id: emptyMessage })}</Paper>
      ) : (
        <Paper sx={{ width: "100%" }}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            columnVisibilityModel={{
              origin: !compactTable,
              payment_type: !compactTable,
            }}
            loading={loading}
            paginationMode="server"
            rowCount={pagination.total || 0}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            disableColumnMenu
            onRowClick={({ row }) => openRegistration(row)}
            sx={{ cursor: "pointer", "& .MuiDataGrid-cell": { minWidth: 0 } }}
          />
        </Paper>
      )}

      {selectedRow && (
        <Dialog open onClose={closeRegistration} disableAutoFocus fullWidth maxWidth="md">
          <EditParticipantModal
            participantId={selectedRow.participant?.id}
            tournament={selectedRow.tournament}
            onSave={() => {
              closeRegistration();
              fetchRegistrations();
            }}
          />
        </Dialog>
      )}
    </Stack>
  );
};

export default CompetitionRegistrationsPage;
